import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import PropTypes from 'prop-types';

const spring = {
	type: 'spring',
	damping: 20,
	stiffness: 300,
};

const defaultColors = ['#191919', '#4a4a4a', '#195c8c', '#199ee0'];

const shuffle = (array) => [...array].sort(() => Math.random() - 0.5);

const Loader = ({
	isLoading = false,
	boxSize = 50,
	containerWidth = 150,
	blurStrength = 3,
	colors = defaultColors,
}) => {
	const indices = colors.map((_, i) => i);
	const [order, setOrder] = useState(indices);
	const [visible, setVisible] = useState(false);
	const [startTime, setStartTime] = useState(null);

	// Handle shuffle animation
	useEffect(() => {
		if (!visible) return;

		// Immediately shuffle once to trigger first animation frame
		setOrder(shuffle(indices));

		const intervalId = setInterval(() => {
			setOrder(shuffle(indices));
		}, 1000);

		return () => clearInterval(intervalId);
	}, [visible]);

	// Control visibility with delay logic
	useEffect(() => {
		let timeoutId;

		if (isLoading) {
			setStartTime(Date.now());
			setVisible(true);
		} else if (startTime) {
			const elapsed = Date.now() - startTime;
			const remaining = 1500 - elapsed;

			timeoutId = setTimeout(() => {
				setVisible(false);
				setStartTime(null);
			}, Math.max(remaining, 0));
		}

		return () => clearTimeout(timeoutId);
	}, [isLoading]);

	if (!visible) return null;

	return (
		<div style={styles.overlay}>
			<div
				style={{
					...styles.blurBackground,
					backdropFilter: `blur(${blurStrength}px)`,
				}}
			/>
			<ul style={{ ...styles.container, width: containerWidth }}>
				{order.map((idx) => (
					<motion.li
						key={idx}
						layout
						initial={{ opacity: 0, scale: 0.8 }}
						animate={{ opacity: 1, scale: 1 }}
						transition={spring}
						style={{
							...styles.item,
							width: boxSize,
							height: boxSize,
							...(idx === 0 ? styles.itemFirst : { backgroundColor: colors[idx] }),
						}}
					/>
				))}
			</ul>
		</div>
	);
};

const styles = {
	overlay: {
		position: 'fixed',
		zIndex: 9999,
		top: 0,
		left: 0,
		width: '100vw',
		height: '100vh',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		pointerEvents: 'none',
	},
	blurBackground: {
		position: 'absolute',
		width: '100%',
		height: '100%',
		backgroundColor: 'rgba(0, 0, 0, 0.1)',
		pointerEvents: 'auto',
	},
	container: {
		position: 'relative',
		listStyle: 'none',
		padding: 0,
		margin: 0,
		display: 'flex',
		gap: '10px',
		flexWrap: 'wrap',
		justifyContent: 'center',
		alignItems: 'center',
		zIndex: 1,
	},
	item: {
		borderRadius: '5px',
	},
	itemFirst: {
		backgroundColor: 'transparent',
		borderRight: '5px solid #191919',
		borderBottom: '5px solid #191919',
		borderTopLeftRadius: 0,
		borderTopRightRadius: 0,
		borderBottomLeftRadius: 0,
		borderBottomRightRadius: '10px',
	},
};

Loader.propTypes = {
	isLoading: PropTypes.bool.isRequired,
	boxSize: PropTypes.number,
	containerWidth: PropTypes.number,
	blurStrength: PropTypes.number,
	colors: PropTypes.arrayOf(PropTypes.string),
};

Loader.defaultProps = {
	boxSize: 50,
	containerWidth: 150,
	blurStrength: 3,
	colors: ['#191919', '#4a4a4a', '#195c8c', '#199ee0'],
};

export default Loader;
